import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Sparkles, Zap } from 'lucide-react';

const ACTIONS = [
  "just realized",
  "had a breakthrough about",
  "changed their mind on",
  "connected the dots on",
  "shifted perspective on"
];

const TOPICS = [
  "React Hooks", "System Design", "User Research", "Climate Change", 
  "Quantum Physics", "Ancient History", "Behavioral Economics", 
  "Machine Learning", "Typography", "Urban Planning"
];

const LOCATIONS = [
  "London, UK", "Tokyo, Japan", "Berlin, Germany", "Toronto, Canada",
  "Sydney, Australia", "Paris, France", "Mumbai, India", "São Paulo, Brazil",
  "Stockholm, Sweden", "Seoul, South Korea"
];

interface Activity {
  id: string;
  user: string;
  action: string;
  topic: string;
  location: string;
  time: string;
}

export const LiveFeed: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    // Initial activity
    addActivity();

    // Random interval for new activities
    const interval = setInterval(() => {
      if (Math.random() > 0.6) {
        addActivity();
      }
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const addActivity = () => {
    const newActivity = {
      id: crypto.randomUUID(),
      user: `User ${Math.floor(Math.random() * 1000)}`,
      action: ACTIONS[Math.floor(Math.random() * ACTIONS.length)],
      topic: TOPICS[Math.floor(Math.random() * TOPICS.length)],
      location: LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)],
      time: 'Just now'
    };

    setActivities(prev => [newActivity, ...prev].slice(0, 3));
  };

  return (
    <div className="fixed bottom-24 left-4 z-40 flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {activities.map((activity) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, x: -20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 w-72 pointer-events-auto"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white shadow-sm">
                <Zap className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-0.5">
                  <MapPin className="w-3 h-3" />
                  {activity.location} • {activity.time}
                </p>
                <p className="text-sm text-gray-900 dark:text-white leading-snug">
                  <span className="font-bold">{activity.user}</span> {activity.action} <span className="text-blue-600 dark:text-blue-400 font-medium">{activity.topic}</span>
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
